import React, { useState } from "react";
import { FileText, MoreVertical, Edit, Trash, Copy, Plus } from "lucide-react";
import {
  useGetMessaeTemplateQuery,
  useAddMessageTemplateMutation,
  useDeleteMessageTemplateMutation,
  useUpdateMessageTemplateMutation,
} from '../../lib/apiSlice/communication/communicationSlice.tsx';
import { getUserIdFromToken } from '../../../../serverUrl';

export default function Templates() {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [newTemplate, setNewTemplate] = useState({
    title: "",
    text: "",
  });
    const userId = getUserIdFromToken()
  
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const { data: messageTemplates, isLoading, error, refetch } = useGetMessaeTemplateQuery();
  const [addTemplate, { isLoading: isCreating }] = useAddMessageTemplateMutation();
  const [deleteTemplate, { isLoading: isDeleting }] = useDeleteMessageTemplateMutation();
  const [updateTemplate, { isLoading: isUpdating }] = useUpdateMessageTemplateMutation();

  const handleCreateTemplate = async () => {
    const payloadTemplate = {
      title: newTemplate.title,
      text: newTemplate.text,
      created_by_message_template: userId,
      updated_by_message_template: userId,
    };
    try {
      await addTemplate(payloadTemplate).unwrap();
      setIsCreateOpen(false);
      setNewTemplate({ title: "", text: "" });
      refetch();
    } catch (err) {
      console.error("Failed to create template:", err);
    }
  };

  const handleEditTemplate = async () => {
    if (!selectedTemplate) return;

    try {
      await updateTemplate({
        id: selectedTemplate.id,
        ...selectedTemplate
      }).unwrap();
      setIsEditOpen(false);
      refetch();
    } catch (err) {
      console.error("Failed to update template:", err);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!selectedTemplate) return;

    try {
      await deleteTemplate(selectedTemplate.id).unwrap();
      setIsDeleteOpen(false);
      refetch();
    } catch (err) {
      console.error("Failed to delete template:", err);
    }
  };

  // Handle pagination
  const totalPages = Math.ceil((messageTemplates?.results?.length || 0) / itemsPerPage);
  const paginatedTemplates = messageTemplates?.results?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  ) || [];

  // Extract variables from template content
  const extractVariables = (content) => {
    const regex = /\{([^}]+)\}/g;
    const matches = content.match(regex);
    return matches ? [...new Set(matches)] : [];
  };

  // Handle variable click in template content
  const handleVariableClick = (variable, isEditMode = false) => {
    if (isEditMode) {
      setSelectedTemplate(prev => ({
        ...prev,
        text: prev.text + ` ${variable}`
      }));
    } else {
      setNewTemplate(prev => ({
        ...prev,
        text: prev.text + ` ${variable}`
      }));
    }
  };

  return (
    <div className="space-y-1 p-1">
      <div className="flex justify-between items-center">
        <h1 className="font-semibold text-gray-800 text-2xl">Message Templates</h1>
        <button
          onClick={() => setIsCreateOpen(true)}
          className="flex items-center bg-blue-500 hover:bg-blue-600 px-4 py-2 rounded-md text-white"
        >
          {isCreating ? (
            <Spinner size="small" />
          ) : (
            <>
              <Plus className="mr-2 w-4 h-4" /> Create Template
            </>
          )}
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <Spinner />
        </div>
      ) : error ? (
        <div className="bg-red-50 p-4 rounded-md text-red-700">
          Error loading templates: {error.message}
        </div>
      ) : (
        <>
          <div className="bg-white shadow-lg mt-3 rounded-md" >
            <table className="divide-y divide-gray-200 min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">
                    Template Name
                  </th>
                  <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">
                    Content Preview
                  </th>
                  <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 w-[100px] font-medium text-gray-500 text-xs text-left uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedTemplates.length > 0 ? (
                  paginatedTemplates.map((template) => (
                    <tr key={template.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <FileText className="mr-2 w-4 h-4 text-blue-500" />
                          <span className="font-medium text-gray-900">{template.title}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-500 text-sm">
                        <div className="line-clamp-2">
                          {template.text.split(/(\{[^}]+\})/).map((part, i) =>
                            part.match(/\{[^}]+\}/) ? (
                              <span
                                key={i}
                                className="text-blue-500 hover:underline cursor-pointer"
                                onClick={() => handleVariableClick(part)}
                              >
                                {part}
                              </span>
                            ) : (
                              part
                            )
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-500 text-sm whitespace-nowrap">
                        {new Date(template.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-right whitespace-nowrap">
                        <div className="relative">
                          <button
                            onClick={() => setDropdownOpen(dropdownOpen === template.id ? null : template.id)}
                            className="flex justify-center items-center p-0 rounded-full w-8 h-8 text-gray-400 hover:text-gray-500"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                          {dropdownOpen === template.id && (
                            <div className="right-0 z-10 absolute bg-white shadow-lg mt-2 border border-gray-200 rounded-md w-48">
                              <div className="py-1">
                                <button
                                  onClick={() => {
                                    setSelectedTemplate(template);
                                    setIsEditOpen(true);
                                    setDropdownOpen(null);
                                  }}
                                  className="flex items-center hover:bg-gray-100 px-4 py-2 w-full text-gray-700 text-sm text-left"
                                >
                                  <Edit className="mr-2 w-4 h-4" />
                                  Edit
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedTemplate(template);
                                    setIsDeleteOpen(true);
                                    setDropdownOpen(null);
                                  }}
                                  className="flex items-center hover:bg-gray-100 px-4 py-2 w-full text-gray-700 text-sm text-left"
                                >
                                  <Trash className="mr-2 w-4 h-4" />
                                  Delete
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-6 py-4 text-gray-500 text-center">
                      No templates available. Create one to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-4">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="disabled:opacity-50 px-4 py-2 border rounded-md"
              >
                Previous
              </button>
              <span className="text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="disabled:opacity-50 px-4 py-2 border rounded-md"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Create Template Dialog */}
      {isCreateOpen && (
        <div className="z-50 fixed inset-0 flex justify-center items-center bg-black bg-opacity-25">
          <div className="bg-white shadow-xl mx-4 rounded-lg w-full max-w-2xl">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-semibold text-xl">Create New Template</h2>
                <button
                  onClick={() => setIsCreateOpen(false)}
                  className="text-gray-400 hover:text-gray-500"
                  disabled={isCreating}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-4 py-4">
                <div>
                  <p htmlFor="title" className="block mb-1 font-medium text-sm">
                    Template Name
                  </p>
                  <input
                    id="title"
                    value={newTemplate.title}
                    onChange={(e) => setNewTemplate({ ...newTemplate, title: e.target.value })}
                    placeholder="e.g., Rent Reminder"
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                  />
                </div>

                <div>
                  <p htmlFor="content" className="block mb-1 font-medium text-sm">
                    Template Content
                  </p>
                  <textarea
                    id="content"
                    value={newTemplate.text}
                    onChange={(e) => setNewTemplate({ ...newTemplate, text: e.target.value })}
                    placeholder="Type your message template here. Use {variable_name} for dynamic content."
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full min-h-[150px]"
                  />
                  <div className="mt-2">
                    <p className="mb-1 font-medium text-sm">Available variables:</p>
                    <div className="flex flex-wrap gap-2">
                      {['{tenant_name}', '{property_name}', '{unit_number}', '{rent_amount}', '{due_date}'].map((variable) => (
                        <button
                          key={variable}
                          onClick={() => handleVariableClick(variable)}
                          className="bg-blue-100 hover:bg-blue-200 px-2 py-1 rounded-md text-blue-700 text-sm"
                        >
                          {variable}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => setIsCreateOpen(false)}
                  className="hover:bg-gray-50 px-4 py-2 border border-gray-300 rounded-md text-gray-700"
                  disabled={isCreating}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateTemplate}
                  className="flex items-center bg-green-500 hover:bg-green-600 px-4 py-2 rounded-md text-white"
                  disabled={isCreating}
                >
                  {isCreating ? (
                    <Spinner size="small" />
                  ) : (
                    'Create Template'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Template Dialog */}
      {isEditOpen && selectedTemplate && (
        <div className="z-50 fixed inset-0 flex justify-center items-center bg-black bg-opacity-25">
          <div className="bg-white shadow-xl mx-4 rounded-lg w-full max-w-2xl">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-semibold text-xl">Edit Template</h2>
                <button
                  onClick={() => setIsEditOpen(false)}
                  className="text-gray-400 hover:text-gray-500"
                  disabled={isUpdating}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-4 py-4">
                <div>
                  <p htmlFor="edit-title" className="block mb-1 font-medium text-sm">
                    Template Name
                  </p>
                  <input
                    id="edit-title"
                    value={selectedTemplate.title}
                    onChange={(e) => setSelectedTemplate({ ...selectedTemplate, title: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                  />
                </div>

                <div>
                  <p htmlFor="edit-content" className="block mb-1 font-medium text-sm">
                    Template Content
                  </p>
                  <textarea
                    id="edit-content"
                    value={selectedTemplate.text}
                    onChange={(e) => setSelectedTemplate({ ...selectedTemplate, text: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full min-h-[150px]"
                  />
                  <div className="mt-2">
                    <p className="mb-1 font-medium text-sm">Available variables:</p>
                    <div className="flex flex-wrap gap-2">
                      {extractVariables(selectedTemplate.text).map((variable) => (
                        <button
                          key={variable}
                          onClick={() => handleVariableClick(variable, true)}
                          className="bg-blue-100 hover:bg-blue-200 px-2 py-1 rounded-md text-blue-700 text-sm"
                        >
                          {variable}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => setIsEditOpen(false)}
                  className="hover:bg-gray-50 px-4 py-2 border border-gray-300 rounded-md text-gray-700"
                  disabled={isUpdating}
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditTemplate}
                  className="flex items-center bg-green-500 hover:bg-green-600 px-4 py-2 rounded-md text-white"
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <Spinner size="small" />
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {isDeleteOpen && selectedTemplate && (
        <div className="z-50 fixed inset-0 flex justify-center items-center bg-black bg-opacity-25">
          <div className="bg-white shadow-xl mx-4 rounded-lg w-full max-w-md">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-semibold text-xl">Confirm Deletion</h2>
                <button
                  onClick={() => setIsDeleteOpen(false)}
                  className="text-gray-400 hover:text-gray-500"
                  disabled={isDeleting}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="mb-6 text-gray-500">
                Are you sure you want to delete the template "{selectedTemplate.title}"? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => setIsDeleteOpen(false)}
                  className="hover:bg-gray-50 px-4 py-2 border border-gray-300 rounded-md text-gray-700"
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteTemplate}
                  className="flex items-center bg-red-500 hover:bg-red-600 px-4 py-2 rounded-md text-white"
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <Spinner size="small" />
                  ) : (
                    'Delete Template'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Spinner component using Tailwind CSS
function Spinner({ size = 'medium' }) {
  const sizeClasses = {
    small: 'h-4 w-4',
    medium: 'h-6 w-6',
    large: 'h-8 w-8',
  };

  return (
    <div className={`animate-spin rounded-full border-2 border-solid border-current border-r-transparent ${sizeClasses[size]}`}>
      <span className="sr-only">Loading...</span>
    </div>
  );
} 
